import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Link as MUILink,
  CircularProgress,
  Grid,
  Pagination,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Paper,
  Chip,
  Zoom,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useDispatch, useSelector } from "react-redux";
import {
  createBanner,
  getRestaurantBanner,
  deleteBanner,
} from "../../redux/slices/bannerSlice";
import { selectUser } from "../../redux/slices/authSlice";

const HomeBanner = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const { loading, banners: uploadedImages } = useSelector(
    (state) => state.banner,
  );
  const restaurantId = user?._id;
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    if (restaurantId) {
      dispatch(getRestaurantBanner());
    }
  }, [dispatch, restaurantId]);

  // Cleanup preview URLs
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    uploadedImages?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((uploadedImages?.length || 0) / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const VisuallyHiddenInput = styled("input")({
    display: "none",
  });

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);

    // Create preview URLs
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviewUrls((prev) => [...prev, ...urls]);
    setImages((prev) => [...prev, ...files]);
  };

  const handleRemovePreview = (index) => {
    // Revoke the URL to avoid memory leaks
    URL.revokeObjectURL(previewUrls[index]);

    // Remove from both arrays
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (images.length === 0) return;

    const formData = new FormData();
    formData.append("restaurant", restaurantId);

    images.forEach((file) => {
      formData.append("images", file);
    });

    await dispatch(createBanner(formData));

    // Cleanup preview URLs
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setImages([]);
    setPreviewUrls([]);
    dispatch(getRestaurantBanner());
  };

  const handleDelete = async (id) => {
    await dispatch(deleteBanner(id));
    dispatch(getRestaurantBanner());
  };

  return (
    <Box sx={{ maxWidth: "1400px", mb:4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a365d" }}>
            Home Banners
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your restaurant's banner images. Upload, preview, and
            organize your promotional banners.
          </Typography>
        </Box>
      </Box>

      {/* Upload Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "20px",
          background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: 3,
            }}
          >
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="banner-upload"
              type="file"
              multiple
              onChange={handleImageUpload}
            />
            <label
              htmlFor="banner-upload"
              style={{ width: "100%", maxWidth: "300px" }}
            >
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{
                  height: "120px",
                  border: "2px dashed #667eea",
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  backgroundColor: "rgba(102, 126, 234, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                    border: "2px dashed #764ba2",
                  },
                }}
              >
                <AddPhotoAlternateIcon
                  sx={{ fontSize: 40, color: "#667eea" }}
                />
                <Typography sx={{ color: "#667eea", fontWeight: 500 }}>
                  Click to Upload
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  PNG, JPG, GIF up to 10MB
                </Typography>
              </Button>
            </label>

            {/* Preview Section */}
            {previewUrls.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "#555", fontWeight: 600 }}
                >
                  Preview ({previewUrls.length} images)
                </Typography>
                <Grid container spacing={2}>
                  {previewUrls.map((url, index) => (
                    <Grid item key={index}>
                      <Fade in={true}>
                        <Box sx={{ position: "relative" }}>
                          <Box
                            component="img"
                            src={url}
                            sx={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "12px",
                              objectFit: "cover",
                              border: "3px solid white",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePreview(index)}
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "#ff4444",
                              color: "white",
                              "&:hover": { backgroundColor: "#cc0000" },
                              padding: "4px",
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Upload Banners"
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Gallery Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: "20px",
          background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                color: "#2C3E50",
              }}
            >
              Banner Gallery
            </Typography>
            {uploadedImages?.length > 0 && (
              <Typography variant="body2" sx={{ color: "#999", mt: 0.5 }}>
                {uploadedImages.length} image
                {uploadedImages.length > 1 ? "s" : ""} total
              </Typography>
            )}
          </Box>

          {uploadedImages?.length > 0 && (
            <Chip
              label={`Showing ${indexOfFirstItem + 1}-${Math.min(
                indexOfLastItem,
                uploadedImages.length,
              )} of ${uploadedImages.length}`}
              sx={{
                bgcolor: "#f0f0f0",
                color: "#666",
                fontWeight: 500,
              }}
            />
          )}
        </Box>

        {currentItems.length > 0 ? (
          <Grid container spacing={3}>
            {currentItems.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={image._id || index}>
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 16px 30px rgba(102, 126, 234, 0.3)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={image.imageUrl}
                      alt={`Banner ${index + 1}`}
                      sx={{
                        objectFit: "cover",
                        transition: "transform 0.5s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    />
                    <CardActions
                      sx={{
                        p: 2,
                        background:
                          "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        Banner #{index + 1 + indexOfFirstItem}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(image?._id)}
                        disabled={loading}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#ff4444",
                            color: "white",
                            borderColor: "#ff4444",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              background: "linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)",
              borderRadius: "16px",
            }}
          >
            <ImageIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#999", mb: 1 }}>
              No Banners Yet
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Upload your first banner image to get started
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "1rem",
                },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HomeBanner;
